import React, { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import {
  TextField,
  Grid,
  Alert,
  InputAdornment,
  IconButton,
  MenuItem,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Typography
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import ScrollToTop from '../common/ScrollToTop';
import AccountStepper from './shared/AccountStepper';
import { checkEmailExists } from './shared/emailValidator';
import {
  AccountSetupStepShell,
  AccountSetupStepContainer,
  AccountSetupForm,
  AccountSetupButtonRow,
  AccountSetupBackButton,
  AccountSetupNextButton,
} from './shared/accountSetupStepStyles';
import { theme } from '../../styles/theme';

const LegalLinks = styled.p`
  text-align: center;
  font-size: 0.875rem;
  color: var(--login-form-muted);
  margin-top: 2rem;

  a {
    color: var(--login-primary);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const EmailAvailableMessage = styled.span`
  color: #4caf50;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  margin-top: 3px;
  margin-left: 14px;
`;

// Orange Lock Icon Component
const OrangeLockIcon = styled(LockIcon)`
  color: #E67E22;  /* Warm, professional orange color */
`;

// Constants
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Mountain Time - Arizona (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' }
];

// Format phone number to E.164
const formatPhoneNumberE164 = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Check if it already has a country code
  if (cleaned.startsWith('1') && cleaned.length === 11) {
    return `+${cleaned}`;
  } else if (cleaned.length === 10) {
    // Assume US number and add +1
    return `+1${cleaned}`;
  }
  return '';
};

// Format phone number for display
const formatPhoneNumberForDisplay = (value) => {
  if (!value) return '';
  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const BusinessInformationStep = ({ onNext, onBack, initialData }) => {
  // Form state
  const [formData, setFormData] = useState(() => ({
    // Account Details
    email: initialData?.email || '',
    password: '',
    confirmPassword: '',
    
    // Business Information
    companyName: initialData?.companyName || '',
    businessEmail: initialData?.businessEmail || '',
    businessPhone: initialData?.businessPhone || '',
    businessPhoneE164: initialData?.businessPhoneE164 || '',
    personalPhone: initialData?.personalPhone || '',
    personalPhoneE164: initialData?.personalPhoneE164 || '',
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    timezone: initialData?.timezone || 'America/New_York',
    
    // Address (Optional during setup)
    address: {
      street1: initialData?.address?.street1 || '',
      city: initialData?.address?.city || '',
      state: initialData?.address?.state || '',
      zipCode: initialData?.address?.zipCode || ''
    },
    sameAsBillingAddress: initialData?.sameAsBillingAddress ?? true
  }));

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isEmailAvailable, setIsEmailAvailable] = useState(false);

  const validateEmail = useCallback(async (email) => {
    if (!email) return false;
    
    setIsCheckingEmail(true);
    setEmailError('');
    setIsEmailAvailable(false);
    
    try {
      const exists = await checkEmailExists(email);
      if (exists === true) {
        setEmailError('This email is already registered. Please use a different email or sign in to your existing account.');
        setIsEmailAvailable(false);
        return false;
      } else if (exists === null) {
        setEmailError('Please enter a valid email address');
        setIsEmailAvailable(false);
        return false;
      }
      // Email is valid and doesn't exist
      setIsEmailAvailable(true);
      return true;
    } catch (error) {
      console.error('Error validating email:', error);
      setEmailError('An error occurred while checking the email. Please try again.');
      return false;
    } finally {
      setIsCheckingEmail(false);
    }
  }, []);

  // Auto-validate pre-filled email on mount (e.g., when returning from previous step)
  useEffect(() => {
    if (formData.email && !isEmailAvailable && !emailError) {
      validateEmail(formData.email.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    
    if (name === 'email') {
      // Clear validation states when email changes
      setEmailError('');
      setIsEmailAvailable(false);
    }
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleEmailBlur = async () => {
    const email = formData.email.trim();
    if (!email) return;

    await validateEmail(email);
  };

  const handlePhoneChange = (event, type) => {
    const displayValue = formatPhoneNumberForDisplay(event.target.value);
    const e164Value = formatPhoneNumberE164(event.target.value);
    
    setFormData(prev => ({
      ...prev,
      [`${type}Phone`]: displayValue,
      [`${type}PhoneE164`]: e164Value
    }));
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all required account fields');
      return false;
    }

    if (emailError) {
      setError('Please fix the email error before continuing');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (!formData.companyName || !formData.firstName || !formData.lastName || !formData.businessPhone) {
      setError('Please fill in all required business information fields');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (validateForm()) {
      // Keep both phone formats for both phone types
      const submissionData = {
        ...formData,
        // Business phone
        businessPhone: formData.businessPhone,
        businessPhoneE164: formData.businessPhoneE164 || formData.businessPhone,
        // Personal phone
        personalPhone: formData.personalPhone,
        personalPhoneE164: formData.personalPhoneE164 || formData.personalPhone
      };
      onNext(submissionData);
    }
  };

  return (
    <AccountSetupStepShell>
      <ScrollToTop />
      <AccountSetupStepContainer>
        <AccountStepper currentStep={2} />

        <AccountSetupForm onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Account Creation Section */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                error={!!emailError}
                helperText={emailError || (isEmailAvailable && formData.email && 'This email is available.')}
                disabled={isCheckingEmail}
                sx={{
                  '& .MuiFormHelperText-root': {
                    color: emailError ? 'error.main' : (isEmailAvailable ? '#4caf50' : 'inherit'),
                    fontWeight: (emailError || isEmailAvailable) ? 500 : 400,
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {isCheckingEmail ? (
                        <CircularProgress 
                          size={20} 
                          sx={{ 
                            marginRight: 1,
                            color: 'primary.main'
                          }} 
                        />
                      ) : isEmailAvailable && formData.email ? (
                        <CheckCircleIcon 
                          sx={{ 
                            color: '#4caf50',
                            marginRight: 1
                          }} 
                        />
                      ) : null}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <OrangeLockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <OrangeLockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Business Information Section */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Company Name"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Business Phone"
                name="businessPhone"
                value={formData.businessPhone}
                onChange={(e) => handlePhoneChange(e, 'business')}
                placeholder="(555) 555-5555"
                helperText="Required for business communications"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile Phone (Optional)"
                name="personalPhone"
                value={formData.personalPhone}
                onChange={(e) => handlePhoneChange(e, 'personal')}
                placeholder="(555) 555-5555"
                helperText="Optional alternate contact number"
              />
            </Grid>



            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleChange}
              >
                {TIMEZONES.map((timezone) => (
                  <MenuItem key={timezone.value} value={timezone.value}>
                    {timezone.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Optional Address Section */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                name="address.street1"
                value={formData.address.street1}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={US_STATES}
                getOptionLabel={(option) => option.label}
                value={US_STATES.find(state => state.value === formData.address.state) || null}
                onChange={(event, newValue) => {
                  handleChange({
                    target: {
                      name: 'address.state',
                      value: newValue ? newValue.value : ''
                    }
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                name="address.zipCode"
                value={formData.address.zipCode}
                onChange={handleChange}
              />
            </Grid>

            {/* Add checkbox for billing address */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="sameAsBillingAddress"
                    checked={formData.sameAsBillingAddress}
                    onChange={(e) => {
                      handleChange({
                        target: {
                          name: 'sameAsBillingAddress',
                          value: e.target.checked
                        }
                      });
                    }}
                  />
                }
                label={
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.colors.text.primary,
                      fontSize: '0.875rem'
                    }}
                  >
                    Use this address as billing address
                  </Typography>
                }
                sx={{
                  '& .MuiFormControlLabel-label': {
                    color: theme.colors.text.primary
                  }
                }}
              />
            </Grid>
          </Grid>

          <LegalLinks>
            By continuing, you agree to our{' '}
            <a href="/main/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
            {', '}
            <a href="/main/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            {', '}
            <a href="/main/cookie-policy" target="_blank" rel="noopener noreferrer">Cookie Policy</a>
            {' '}and{' '}
            <a href="/main/refund-policy" target="_blank" rel="noopener noreferrer">Refund Policy</a>
          </LegalLinks>

          <AccountSetupButtonRow>
            <AccountSetupBackButton
              type="button"
              onClick={onBack}
              disabled={isCheckingEmail}
            >
              Back
            </AccountSetupBackButton>
            <AccountSetupNextButton
              type="submit"
              disabled={isCheckingEmail || emailError || !isEmailAvailable}
            >
              Next
            </AccountSetupNextButton>
          </AccountSetupButtonRow>
        </AccountSetupForm>
      </AccountSetupStepContainer>
    </AccountSetupStepShell>
  );
};

export default BusinessInformationStep; 